import { table_content_data } from "@assets/constant/index.constant";
import { FormField } from "@utils/interface/form.interface";
import { SelectProps } from "@utils/interface/index.interface";
import CryptoJS from "crypto-js";
import { format, formatDistance, isValid, parseISO } from "date-fns";
import Cookies from "js-cookie";
import { jwtDecode, JwtPayload } from "jwt-decode";
import React, { ReactNode } from "react";
import { toast } from "sonner";
import * as XLSX from "xlsx";

import {
  File,
  FileImage,
  FileVideo,
  FileText,
  FileSpreadsheet,
  FileAudio,
  FileArchive,
  FileCode,
  FileType,
  FileJson,
} from "lucide-react";

export function getFileIcon(typeOrName: string) {
  if (!typeOrName) return File;

  const type = typeOrName.toLowerCase();

  // --- First check MIME type ---
  if (type.startsWith("image/")) return FileImage;
  if (type.startsWith("video/")) return FileVideo;
  if (type.startsWith("audio/")) return FileAudio;

  if (type === "application/pdf") return FileType;
  if (
    type === "text/csv" ||
    type === "application/vnd.ms-excel" ||
    type.includes("spreadsheet")
  )
    return FileSpreadsheet;
  if (
    type === "application/msword" ||
    type ===
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
  )
    return FileType;
  if (type.includes("json")) return FileJson;
  if (type.includes("zip") || type.includes("rar") || type.includes("tar"))
    return FileArchive;
  if (type.startsWith("text/")) return FileText;
  if (
    type.includes("javascript") ||
    type.includes("typescript") ||
    type.includes("html")
  )
    return FileCode;

  // --- Fallback: check by file extension ---
  const ext = type.split(".").pop() || "";

  switch (ext) {
    case "png":
    case "jpg":
    case "jpeg":
    case "gif":
    case "webp":
      return FileImage;
    case "mp4":
    case "mov":
    case "avi":
      return FileVideo;
    case "mp3":
    case "wav":
      return FileAudio;
    case "pdf":
      return FileType;
    case "csv":
    case "xls":
    case "xlsx":
      return FileSpreadsheet;
    case "doc":
    case "docx":
      return FileType;
    case "zip":
    case "rar":
    case "tar":
    case "gz":
      return FileArchive;
    case "json":
      return FileJson;
    case "txt":
      return FileText;
    case "js":
    case "ts":
    case "html":
    case "css":
      return FileCode;
    default:
      return FileType;
  }
}

const isFn = (fn: any) => typeof fn === "function";
type ToastProps = {
  title: string;
  description: string;
  type: "success" | "error" | "warning";
};
const isNullCheck = (
  data: string | object | number | null | ReactNode
): string | object | number | null | ReactNode => {
  return !data ? "N/A" : data;
};
const showToast = ({ title, description, type }: ToastProps) => {
  switch (type) {
    case "error":
      return toast.error(title, {
        description,
        closeButton: true,
        classNames: {
          description: "!text-black",
          closeButton: "!absolute !right-0",
          icon: "!mr-14",
        },
      });
    case "success":
      return toast.success(title, {
        description,
        closeButton: true,
        classNames: {
          description: "!text-black",
          closeButton: "!absolute !right-0",
          icon: "!mr-14",
        },
      });
    case "warning":
      return toast.warning(title, {
        description,
        closeButton: true,
        classNames: {
          description: "!text-black",
          closeButton: "!absolute !right-0",
          icon: "!mr-14",
        },
      });
    default:
      return toast.error(title, {
        description,
        closeButton: true,
        classNames: {
          description: "!text-black",
          closeButton: "!absolute !right-0",
          icon: "!mr-14",
        },
      });
  }
};
function maskEmail(email: string): string {
  const [name, domain] = email.split("@");
  if (!name || !domain) return email; // invalid email format
  if (name.length <= 3) return email;
  const visible = name.slice(0, 3);
  const masked = "*".repeat(Math.max(name.length - 3, 1));

  return `${visible}${masked}@${domain}`;
}

const SECRET_KEY = import.meta.env.VITE_SECRET_KEY;

// Function to encrypt data
const encryptData = (data: any): string => {
  const jsonString = JSON.stringify(data);
  const encrypted = CryptoJS.AES.encrypt(jsonString, SECRET_KEY).toString();
  return encrypted;
};

const decryptData = (cipherText: string): any => {
  if (!cipherText) return;
  const bytes = CryptoJS.AES.decrypt(cipherText, SECRET_KEY);
  const decryptedData = bytes.toString(CryptoJS.enc.Utf8);
  return JSON.parse(decryptedData);
};
export const LogoutOfSystem = () => {
  Cookies.remove("access");
  Cookies.remove("refresh");
  sessionStorage.removeItem("access");
  sessionStorage.removeItem("refresh");
  Cookies.remove("email");
};
export function getDecodedToken(
  token: string
): (JwtPayload & { role: number }) | undefined {
  if (!token) return;
  return jwtDecode(token) as JwtPayload & { role: number };
}
const getTableInfo = ({
  mode,
  type,
}: {
  mode: "default" | "add" | "edit";
  type: string;
}): {
  title: string;
  description: string;
} => {
  console.log("this is the mode and type", mode, type);
  return table_content_data[type]?.[mode] || { title: "", description: "" };
};
const removeHttpPrefix = (url: string): string => {
  return url && url.replace(/^(https?:\/\/)/, "");
};
function isEditorContentEmpty(html: string): boolean {
  if (!html) return true;

  const cleaned = html
    .replace(/<p[^>]*><(br|focus)\s*\/?><\/p>/gi, "") // remove <p><br/></p> or <p><focus/></p>
    .replace(/<[^>]*>/g, "") // remove all HTML tags
    .replace(/&nbsp;/gi, "") // remove non-breaking spaces
    .replace(/\s+/g, ""); // remove all other whitespace

  return cleaned.length === 0;
}

function removeHTMLTags(html: string): string {
  if (!html) return "";
  const cleaned = html
    .replace(/<p[^>]*><(br|focus)\s*\/?><\/p>/gi, "") // removes <p><br/></p> or <p><focus/></p>
    .replace(/<[^>]*>/g, "") // remove all HTML tags
    .replace(/&nbsp;/g, " ") // replace &nbsp; with regular space
    .replace(/\s+/g, " ") // replace multiple whitespace characters with single space
    .trim(); // remove leading/trailing whitespace

  return cleaned;
}

function formatNumber(num: number) {
  if (num >= 1_000_000) {
    return (num / 1_000_000).toFixed(1).replace(/\.0$/, "") + " M";
  } else if (num >= 1_000) {
    return (num / 1_000).toFixed(1).replace(/\.0$/, "") + "K";
  } else {
    return num;
  }
}
const formatSelectData = <T>(
  data: Record<string, any> | Record<string, any>[],
  key = "title",
  value = "id",
  secondary_key: ""
): T[] => {
  if (!key || !value) return [];
  if (!data || (Array.isArray(data) && data.length === 0)) return [];

  if (!Array.isArray(data)) {
    return [{ label: isNullCheck(data[key]), value: data[value] }] as T[];
  }

  if (secondary_key?.length > 0) {
    return data.map((item) => ({
      label: isNullCheck(item[key]),
      value: item[value],
      secondary_label: isNullCheck(item[secondary_key]),
    })) as T[];
  }

  return data.map((item) => ({
    label: isNullCheck(item[key]),
    value: item[value],
  })) as T[];
};
const getDefault = ({
  data,
  id,
  slug,
}: {
  data: any[];
  id: string | number;
  slug?: boolean;
}): SelectProps[] => {
  if (slug) {
    return data?.find((item: any) => item.slug == id) || {};
  }
  return data?.find((item: any) => item.id == id) || {};
};

function resetFormSchema(data: any[] = []): object {
  const resetValue = data?.reduce((acc, field) => {
    if (field.name) acc[field.name] = "";
    return acc;
  }, {});
  return resetValue;
}

const throwErrorAlert = (data: any) => {
  console.log("Data", data?.data?.data);
  Object.entries(data?.data?.data || data || {})?.forEach(([key, value]) => {
    showToast({
      description: `${key} : ${value}` || `Failed to delete ${key}`,
      type: "error",
      title: "Error",
    });
  });
};

const createURl = (file: File) => {
  if (!file) return "";
  if (typeof file === "string") return file;
  const url = URL.createObjectURL(file);
  return url;
};

const refineNull: object = (obj: any) => {
  return Object.fromEntries(
    Object.entries(obj).filter(([_, value]) => value !== null)
  );
};

const refineObjType = ({
  data,
  keys,
  type,
}: {
  data: object;
  keys: string[];
  type: "string" | "object" | "number" | "boolean";
}): object => {
  let cleanedData: any = { ...data };
  keys.forEach((key) => {
    if (cleanedData.hasOwnProperty(key) && typeof cleanedData[key] === type) {
      delete cleanedData[key];
    }
  });
  return cleanedData || {};
};

const getMapedValue = (data: any[], key: string): string[] | number[] => {
  if (!data) return [];
  return data?.map((item) => item[key]);
};


function formatDateTime(date: Date | string | undefined | null) {
  if (!date) return null;
  let parsed: Date;

  if (date instanceof Date) {
    parsed = date;
  } else {
    parsed = parseISO(date);
  }

  if (!isValid(parsed)) return null;

  return format(parsed, "MMM d, yyyy | h:mm a");
}


function formatDate(date: Date | string | undefined | null) {
  if (!date) return null;
  if (date instanceof Date && isValid(date)) {
    return format(date, "yyyy-MM-dd");
  }
  const parsedDate = parseISO(date as string);
  return isValid(parsedDate) ? format(parsedDate, "yyyy-MM-dd") : null;
}

function timeAgo(date: Date) {
  const now = new Date();
  const distance = formatDistance(date, now, { addSuffix: true });
  return distance;
}

const handleSuccessResponse = ({
  status,
  updateData,
  list,
  searchParams,
  setSearchParams,
  moduleName,
  slug = false,
  schema,
  reset,
}: {
  status: number;
  updateData: any;
  list: any;
  searchParams: URLSearchParams;
  moduleName: string;
  slug?: boolean;
  setSearchParams: (e: any) => void;
  schema?: FormField[];
  reset?: (e: any) => void;
}) => {
  if (![200, 201].includes(status)) {
    return throwErrorAlert(updateData);
  }

  const successMessages: Record<number, string> = {
    200: `${moduleName} successfully updated`,
    201: `${moduleName} successfully created`,
  };

  if (status === 200) {
    list.data.results = list.data.results.map((item: any) => {
      const key = slug ? "slug" : "id";
      return String(item[key]) === String(searchParams.get("item"))
        ? updateData
        : item;
    });
  } else if (status === 201) {
    list.data.results.unshift(updateData);
    list.data.results = list.data.results.slice(0, 20);
  }

  if (searchParams.get("mode")) {
    searchParams.delete("mode");
    setSearchParams(() => searchParams);
  }

  if (successMessages[status]) {
    reset && reset(resetFormSchema(schema));
    return toast.success(successMessages[status]);
  }
};

function getUniqueArray(array: any[]): any[] {
  const uniqueArray: any[] = [];
  array?.forEach((item) => {
    if (!uniqueArray?.find((i) => i.id === item.id)) {
      uniqueArray.push(item);
    }
  });
  return uniqueArray;
}
function getUrlObj(obj: Record<string, any>): string | null {
  const urlRegex = /^(https?|ftp):\/\/[^\s/$.?#].[^\s]*$/;
  for (const key of Object.keys(obj)) {
    if (typeof obj[key] === "string" && urlRegex.test(obj[key])) {
      return key;
    }
  }
  return null;
}

function getServerFileDetails(url: string): {
  filename: string;
  extension: string;
  file: string;
} {
  if (!url) {
    return { filename: "", extension: "", file: "" };
  }

  const segments = url.split("/");
  const lastSegment = segments.pop() || "";

  const dotIndex = lastSegment.lastIndexOf(".");
  if (dotIndex === -1) {
    return { filename: lastSegment, extension: "", file: lastSegment };
  }

  const filename = lastSegment.substring(0, dotIndex);
  const extension = lastSegment.substring(dotIndex + 1);
  const file = lastSegment;
  return { file, filename, extension };
}

function ensureProtocol(url: string) {
  if (!/^https?:\/\//i.test(url)) {
    return `https://${url}`;
  }
  return url;
}

const getSubmitAction = ({
  searchParams,
  isPending,
  sufix,
}: {
  searchParams: any;
  isPending?: boolean;
  sufix: string;
}) => {
  const type = searchParams.get("mode") || searchParams.get("page");
  if (isPending) return "Submitting ...";
  if (type === "add") return "Add " + sufix;
  if (type === "edit") return "Save " + sufix;
  return "Add " + sufix;
};

const getIconInfo = ({
  searchParams,
  icon,
}: {
  searchParams: any;
  icon: React.ReactSVGElement | any;
}) => {
  const type = searchParams.get("mode") || searchParams.get("page");
  if (type === "add") return icon;
  if (type === "edit") return null;
  else return icon;
};

type LevelKey = "phd" | "master" | "masters" | "bachelors" | "bachelor";

const levelColors: Record<LevelKey, string> = {
  phd: "green",
  master: "yellow",
  masters: "yellow",
  bachelors: "orange",
  bachelor: "orange",
};
const getChipInfo = (level: string): string => {
  const key = level?.toLowerCase();
  if (key in levelColors) {
    return levelColors[key as LevelKey];
  }
  return "gray";
};

const getSorted = (data: any[]): any[] => {
  return data?.length > 0 ? data?.sort((a, b) => a.position - b.position) : [];
};
const getObjQuery = (data: object): string => {
  if (!data || Object.keys(data).length === 0) return "";
  return Object.entries(data)
    .map(([key, value]) => `${key}=${value}`)
    .join("&");
};

function isFileOrBlob(value: any): value is File | Blob {
  return value instanceof File || value instanceof Blob;
}

// function exportToExcel(
//   data: Record<string, any>[],
//   fileName: string = "output.xlsx"
// ): void {
//   if (!data || data.length === 0) {
//     console.error("No data provided to export.");
//     return;
//   }

//   // Get all unique keys from all objects
//   const allKeys = Array.from(
//     new Set(data.flatMap((item) => Object.keys(item)))
//   );

//   // Flatten values (join arrays into comma-separated strings)
//   const flattenedData = data.map((item) => {
//     const row: Record<string, any> = {};
//     allKeys.forEach((key) => {
//       const value = item[key];
//       if (Array.isArray(value)) {
//         row[key] = value.join(", "); // Join arrays with commas
//       } else {
//         row[key] = value ?? ""; // Handle undefined/null
//       }
//     });
//     return row;
//   });

//   // Create header and rows
//   const aoa = [
//     allKeys,
//     ...flattenedData.map((item) => allKeys.map((key) => item[key])),
//   ];

//   const ws = XLSX.utils.aoa_to_sheet(aoa);

//   const wb = XLSX.utils.book_new();
//   XLSX.utils.book_append_sheet(wb, ws, "Sheet1");

//   XLSX.writeFile(wb, fileName);
// }

function exportToExcel(
  data: Record<string, any>[],
  fileName: string = "output.xlsx"
): void {
  if (!data || data.length === 0) {
    console.error("No data provided to export.");
    return;
  }

  // Helper function to flatten nested objects and arrays
  const flattenValue = (value: any, key?: string): string => {
    if (value === null || value === undefined) {
      return "";
    }
    
    if (Array.isArray(value)) {
      // Handle array of objects (like course)
      return value.map(item => {
        if (typeof item === 'object' && item !== null) {
          // For objects in arrays, extract meaningful properties
          if (item.title) return item.title; // For course objects
          if (item.name) return item.name;   // For other objects with name
          if (item.id && item.title) return `${item.title} (ID: ${item.id})`;
          if (item.id && item.name) return `${item.name} (ID: ${item.id})`;
          // If object has multiple properties, join key-value pairs
          return Object.entries(item)
            .filter(([k, v]) => v !== null && v !== undefined)
            .map(([k, v]) => `${k}: ${v}`)
            .join('; ');
        }
        return String(item);
      }).join(", ");
    }
    
    if (typeof value === 'object' && value !== null) {
      // Handle single objects (like source)
      if (value.name) return value.name;     // For source object
      if (value.title) return value.title;   // For other objects with title
      if (value.id && value.name) return `${value.name} (ID: ${value.id})`;
      if (value.id && value.title) return `${value.title} (ID: ${value.id})`;
      
      // For other objects, join key-value pairs
      return Object.entries(value)
        .filter(([k, v]) => v !== null && v !== undefined)
        .map(([k, v]) => `${k}: ${v}`)
        .join('; ');
    }
    
    return String(value);
  };

  // Get all unique keys from all objects
  const allKeys = Array.from(
    new Set(data.flatMap((item) => Object.keys(item)))
  );

  // Flatten the data with proper handling of nested objects
  const flattenedData = data.map((item) => {
    const row: Record<string, any> = {};
    allKeys.forEach((key) => {
      row[key] = flattenValue(item[key], key);
    });
    return row;
  });

  // Create header and rows
  const aoa = [
    allKeys,
    ...flattenedData.map((item) => allKeys.map((key) => item[key])),
  ];

  const ws = XLSX.utils.aoa_to_sheet(aoa);

  // Auto-size columns
  const colWidths = allKeys.map(key => {
    const maxLength = Math.max(
      key.length,
      ...flattenedData.map(row => String(row[key]).length)
    );
    return { width: Math.min(maxLength + 2, 50) }; // Cap at 50 characters
  });
  
  ws['!cols'] = colWidths;

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Sheet1");

  XLSX.writeFile(wb, fileName);
}

function isSameOrderById(arr1: any[], arr2: any[]) {
  if (!arr1 && !arr2) return;
  if (arr1.length !== arr2.length) return false;

  for (let i = 0; i < arr1.length; i++) {
    if (arr1[i].id !== arr2[i].id) {
      return false;
    }
  }

  return true;
}
function makeSlug(key: string): string {
  return key?.trim().toLowerCase().replace(/\s+/g, "-");
}
const changeSymbolstoDash = (data: string) => {
  if (!data) return "";
  return data
    .replace(/[^\w\s-]/g, "") // Remove all special characters except alphanumeric, space, and dash
    .replace(/\s+/g, "-") // Replace spaces with a single dash
    .replace(/-+/g, "-"); // Replace consecutive dashes with a single dash
};
function isFilled(value: string) {
  // Check for null, undefined, empty string, or only whitespace
  return value !== null && value !== undefined && String(value).trim() !== "";
}

function validateData(data: any[]): boolean {
  return data.every((item) => {
    if (item.type === "quotation") {
      // quotation must have title filled and children with question & answer filled
      return (
        isFilled(item.title.trim()) &&
        item.children.every(
          (child: { question: string; answer: string }) =>
            isFilled(child.question.trim()) && isFilled(child.answer.trim())
        )
      );
    }

    if (item.type === "description") {
      // description must have title and description filled
      return isFilled(item.title.trim()) && isFilled(item.description.trim());
    }

    return false; // invalid type
  });
}

function isFutureDate(dateString: string): boolean {
  const date = new Date(dateString);
  const today = new Date();

  // normalize both to remove time part
  today.setHours(0, 0, 0, 0);
  date.setHours(0, 0, 0, 0);

  return date > today;
}

const combineDateAndTime = (date: Date | string, timeString: string): Date => {
  const baseDate = typeof date === "string" ? new Date(date) : new Date(date);
  
  if (!timeString) return baseDate;

  // 1. Match hours, minutes, and AM/PM using regex
  const match = timeString.match(/(\d+):(\d+)\s*(AM|PM)/i);
  
  if (!match) return baseDate;

  let [ , hoursStr, minutesStr, meridiem] = match;
  let hours = parseInt(hoursStr, 10);
  const minutes = parseInt(minutesStr, 10);

  // 2. Convert 12-hour format to 24-hour logic
  if (meridiem.toUpperCase() === "PM" && hours < 12) {
    hours += 12;
  } else if (meridiem.toUpperCase() === "AM" && hours === 12) {
    hours = 0;
  }

  const newDate = new Date(baseDate);
  newDate.setHours(hours, minutes, 0, 0);
  
  return newDate;
};

export {
  createURl,
  changeSymbolstoDash,
  decryptData,
  encryptData,
  ensureProtocol,
  formatDate,
  formatSelectData,
  getChipInfo,
  getDefault,
  getIconInfo,
  getMapedValue,
  getObjQuery,
  getServerFileDetails,
  getSorted,
  getSubmitAction,
  getTableInfo,
  getUniqueArray,
  makeSlug,
  getUrlObj,
  handleSuccessResponse,
  isFileOrBlob,
  isNullCheck,
  maskEmail,
  refineNull,
  isFutureDate,
  refineObjType,
  removeHttpPrefix,
  resetFormSchema,
  isEditorContentEmpty,
  showToast,
  throwErrorAlert,
  timeAgo,
  formatNumber,
  exportToExcel,
  isSameOrderById,
  removeHTMLTags,
  validateData,
  formatDateTime,
  combineDateAndTime
};

export { isFn };
